import { Box, InputAdornment, Stack, TextField, useTheme } from '@mui/material';
import React, { FunctionComponent, useCallback, useEffect, useMemo, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { SearchFillIcon } from 'src/assets/icons/SearchFillIcon';
import { CustomWaterLoading } from 'src/components';
import { useAuthentication } from 'src/features/authentication/context';
import { TagData } from 'src/features/reports/types';
import { ModalContentItem } from 'src/features/trends/components';
import { SelectedItemsList } from 'src/features/trends/components/selected-items-list';
import { SEPARATOR } from 'src/features/trends/config';
import { useDebounce } from 'src/hooks/useDebounce';

interface ITagSelectionInputProps {
  value?: TagData[];
  onChange: (tags: TagData[]) => void;
}

const TagSelectionInput: FunctionComponent<ITagSelectionInputProps> = ({ onChange, value }) => {
  const methods = useForm();
  const [isLoading, setIsLoading] = useState(false);
  const theme = useTheme();
  const values = methods.watch();
  const { sites } = useAuthentication();

  const [filteredItems, setFilteredItems] = useState<any>([]);
  // Track tagIds explicitly deselected so the proxy can reflect that in ModalContentRow
  const [deselectedTagIds, setDeselectedTagIds] = useState<Set<string>>(new Set());

  const selectedTagIds = useMemo(() => new Set((value ?? []).map((t) => t.tagId)), [value]);

  const debouncedSearchTerm = useDebounce(values?.search, 300);
  const sitesForDisplay = debouncedSearchTerm ? filteredItems : sites?.value;

  useEffect(() => {
    const filtered = sites?.value.filter((tag: any) =>
      tag.siteName?.toLowerCase().includes(debouncedSearchTerm)
    );
    if (filtered) setFilteredItems(filtered);
    setIsLoading(false);
  }, [debouncedSearchTerm, sites]);

  const computeSelectedTags = useCallback(
    (updatedValues: Record<string, any>): TagData[] => {
      const fromFormState = Object.entries(updatedValues)
        .filter(([key, val]) => key !== 'search' && key.startsWith('row') && val === 'true')
        .map(([key]) => {
          const parts = key.split(SEPARATOR);
          return {
            siteId: parts[1],
            siteName: parts[2],
            rawInputName: parts[3],
            rawUnit: [null, '', 'null'].includes(parts[4]) ? '' : parts[4],
            gatewayId: parts[5],
            tagId: parts[6],
            tagName: parts[7],
          } as TagData;
        });

      // TagIds that already have a form-state entry (either selected or explicitly deselected)
      const interactedTagIds = new Set(
        Object.entries(updatedValues)
          .filter(([key]) => key !== 'search' && key.startsWith('row'))
          .map(([key]) => key.split(SEPARATOR)[6])
          .filter(Boolean)
      );

      // Preserve initially selected tags that haven't been touched via the accordion
      const preservedInitialTags = (value ?? []).filter((t) => !interactedTagIds.has(t.tagId));

      return [...fromFormState, ...preservedInitialTags];
    },
    [value]
  );

  const setValue = useCallback(
    (name: any, val: any) => {
      // ModalContentItem fires an `events-*` toggle when isParticularChart is falsy.
      // We pass isParticularChart={false} only to make the tag rows visible here; the
      // events entries are irrelevant for the report dialog, so ignore them.
      if (typeof name === 'string' && name.startsWith('events')) return;

      methods.setValue(name, val);

      // Keep deselectedTagIds in sync so the proxy reflects state correctly
      if (typeof name === 'string' && name.startsWith('row')) {
        const tagId = name.split(SEPARATOR)[6];
        if (tagId) {
          setDeselectedTagIds((prev) => {
            const next = new Set(prev);
            val === 'false' ? next.add(tagId) : next.delete(tagId);
            return next;
          });
        }
      }

      const updatedValues = { ...values, [name]: val };
      onChange(computeSelectedTags(updatedValues));
    },
    [onChange, methods, values, computeSelectedTags]
  );

  // Proxy passed to ModalContentItem: matches by tagId for tags that haven't been interacted
  // with yet, so ModalContentRow can find the correct checked state regardless of rawInputName
  // or rawUnit differences between stored TagData and the live TagResponse.
  const proxyValues = useMemo(() => {
    if (!selectedTagIds.size) return values;
    return new Proxy(values as any, {
      get(target, prop) {
        if (typeof prop === 'string' && prop.startsWith(`row${SEPARATOR}`)) {
          const existing = Reflect.get(target, prop);
          if (existing !== undefined) return existing;
          const tagId = prop.split(SEPARATOR)[6];
          if (tagId) {
            if (deselectedTagIds.has(tagId)) return 'false';
            if (selectedTagIds.has(tagId)) return 'true';
          }
        }
        return Reflect.get(target, prop);
      },
    });
  }, [values, selectedTagIds, deselectedTagIds]);

  // Build a values object for SelectedItemsList that includes initially selected tags as chips,
  // even before the user has opened an accordion (where they'd appear in the raw form state).
  const valuesForSelectedItemsList = useMemo(() => {
    if (!value?.length) return values;
    const result: Record<string, any> = {};
    // Initial tags first so their chips appear before any newly added ones
    for (const tag of value) {
      const alreadyTracked = Object.entries(values).some(
        ([key]) =>
          key !== 'search' && key.startsWith('row') && key.split(SEPARATOR)[6] === tag.tagId
      );
      if (!alreadyTracked) {
        const displayKey = `row${SEPARATOR}${tag.siteId}${SEPARATOR}${tag.siteName}${SEPARATOR}${
          tag.rawInputName ?? null
        }${SEPARATOR}${tag.rawUnit || null}${SEPARATOR}${tag.gatewayId}${SEPARATOR}${
          tag.tagId
        }${SEPARATOR}${tag.tagName?.replace(/\./g, '(dot)')}`;
        result[displayKey] = 'true';
      }
    }
    // Then append form-state entries (newly selected tags, deselections, search, etc.)
    Object.entries(values).forEach(([key, val]) => {
      result[key] = val;
    });
    return result;
  }, [values, value]);

  return (
    <FormProvider {...methods}>
      <Stack sx={{ width: '100%' }}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            marginBottom: '10px',
            padding: '10px 20px',
          }}
        >
          <>
            <TextField
              onChange={(event) => {
                methods.setValue('search', event.target.value);
                setIsLoading(true);
              }}
              value={methods.watch('search') ?? ''}
              name="search"
              fullWidth
              placeholder="Search sites..."
              inputProps={{ style: { padding: '10px' }, 'data-sm': 'search-sites' }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchFillIcon fill={theme.palette.text.disabled} />
                  </InputAdornment>
                ),
              }}
            />
            <SelectedItemsList
              values={valuesForSelectedItemsList}
              setValue={setValue}
              sites={sites?.value}
            />
          </>
        </Box>

        {!sites?.loaded || isLoading ? (
          <Stack
            flexDirection={'row'}
            justifyContent={'center'}
            alignItems={'center'}
            sx={{ mb: '16px' }}
          >
            <CustomWaterLoading />
          </Stack>
        ) : (
          <>
            <Box
              sx={{
                maxHeight: '450px',
                overflow: 'visible',
                gap: '5px',
                display: 'flex',
                flexDirection: 'column',
                padding: '0 20px',
              }}
            >
              {sitesForDisplay?.map((site: any) => (
                <ModalContentItem
                  key={site.id}
                  site={site}
                  setValue={setValue}
                  values={proxyValues}
                  isParticularChart={false}
                />
              ))}
            </Box>
          </>
        )}
      </Stack>
    </FormProvider>
  );
};

export default React.memo(TagSelectionInput);
